import { Button } from '@components/button';
import { Card } from '@components/card';
import { useFormContext } from '@components/form';
import { FormInstance } from '@components/form/create-form';
import { HideContainer } from '@components/hide-container';
import { IconArrowDown } from '@components/icons/arrow-down';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconOpen } from '@components/icons/open';
import { IconOutput } from '@components/icons/output';
import { IconTriggerOff } from '@components/icons/trigger-off';
import { InfoBox } from '@components/info-box';
import { SelectOption } from '@components/select';
import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingVariables, FieldType } from '@components/text-editors/addons/field-mapping';
import { fieldTypeIconMap } from '@components/text-editors/addons/field-mapping-type-maps';
import { VisuallyHidden } from '@components/visually-hidden';
import { useScopedTranslate } from '@locale/translate';
import { extractDocstring, variablesFromDocstring } from '@utils/strings';

import { JourneyActionDetailed, TravellerJourneyActionDetailed, VariableValue } from 'api/types';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import { DescriptionFormField, useVariableGroups } from 'ui/journey-builder/node-types/common';

import { useCodeEditorModal } from './editor-modal';

import { TriggerMethod } from '../common';
import { getFieldMappingId } from '../helpers';

export type ActionType = 'code';

export type CodeData = {
  description: string;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
  title: FieldMappingVariables;
  inputs?: FieldMappingVariables[];
  code: string;
};

type CodeFormProps = {
  Form: FormInstance<CodeData>;
  data: JourneyActionDetailed<ActionType> | TravellerJourneyActionDetailed<ActionType>;
};

export const isTravellerData = (
  data: CodeFormProps['data']
): data is TravellerJourneyActionDetailed<ActionType> => {
  return Boolean((data as TravellerJourneyActionDetailed<ActionType>).journeyId);
};

export const CodeFormFields = ({ Form, data }: CodeFormProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const values = data?.values;
  const { store } = useFormContext();
  const journeyId = isTravellerData(data) ? data.journeyId : data.journey;
  const fieldMappingId = getFieldMappingId(journeyId);
  const showEditorModal = useCodeEditorModal();

  function useVariableGroupsByTypes(types?: FieldType) {
    return useVariableGroups({
      actionVariables: data?.actionVariables,
      journeyId,
      ...(types && { types: [types] }),
      excludedTypes: ['file', 'file_multi'],
    });
  }

  const allVariableGroups = useVariableGroupsByTypes();
  const inputs = (store.useFieldValue('inputs') as VariableValue[]) ?? values?.inputs;
  const code = (store.useFieldValue('code') as string) ?? values?.code;
  const docstring = extractDocstring(code ?? '');
  const variables = variablesFromDocstring(docstring);

  const outputVariables = Object.entries(variables ?? {});
  const hasOutputVariables = outputVariables.length > 0;

  return (
    <Stack orientation="vertical" space={8}>
      <HideContainer title={t('actionDetails')}>
        <DescriptionFormField defaultValue={data.description} />
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('triggerMethod')}>
        <ExecutionMethod
          assignees={data.assignees}
          isAutomatic={data.isAutomatic}
          formStore={store}
          hideLabel
        />
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('input')}>
        <Stack orientation="vertical" space={4}>
          <Form.FieldMappingListField
            id="inputs"
            label={t('inputs')}
            defaultValue={values?.inputs}
            variableGroups={allVariableGroups}
            fieldMappingId={fieldMappingId}
          />
          <VisuallyHidden>
            <Form.PythonEditor id="code" label={t('code')} defaultValue={values?.code} />
          </VisuallyHidden>
          <Button
            onClick={() => showEditorModal({ inputs, code, setValue: store.actions.setValue })}
            text={t('openCodeEditor')}
            variant="secondary"
            iconLeft={<IconOpen />}
          />
        </Stack>
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('output')}>
        {hasOutputVariables && (
          <Stack space={4}>
            <Card variant="default" space={4}>
              <Stack space={1}>
                {outputVariables.map(([key, value]) => {
                  const Icon = fieldTypeIconMap[value.type];
                  return (
                    <Stack key={key} orientation="horizontal" space={2}>
                      <Icon color="elementPurpleInvert" />
                      <Text text={value.label.replaceAll("'", '').replaceAll('"', '')} />
                    </Stack>
                  );
                })}
              </Stack>
            </Card>
            <Text font="regular14" text={t('useOutput')} />
          </Stack>
        )}

        {!hasOutputVariables && (
          <Card variant="gray" space={4}>
            <Stack orientation="horizontal" align="center" space={4}>
              <IconTriggerOff color="textSecondary" />
              <Text text={t('noOutputConfigured')} color="textSecondary" font="semibold14" />
            </Stack>
          </Card>
        )}
      </HideContainer>
    </Stack>
  );
};

export const Info = () => {
  const { t } = useScopedTranslate(sidebarLocale);
  return (
    <Stack space={4}>
      <Text font="bold16" text={t('infoTitle')} />
      <Text font="regular14" text={t('infoText')} />
      <Stack space={2} align="center">
        <div style={{ width: '100%' }}>
          <InfoBox variant="green" icon={<IconCheckmark />} text={t('createCustomPrompt')} />
        </div>
        <Stack
          space={4}
          orientation="horizontal"
          justify="space-between"
          align="center"
          style={{ width: '100%' }}
        >
          <Separator />
          <Text font="semibold14" text={t('core.or')} color="textHelper" />
          <Separator />
        </Stack>
        <div style={{ width: '100%' }}>
          <InfoBox variant="green" icon={<IconCheckmark />} text={t('selectField')} />
        </div>
        <IconArrowDown color="elementPurple50" />
        <div style={{ width: '100%' }}>
          <InfoBox variant="indigo" icon={<IconOutput />} text={t('infoOutputText')} />
        </div>
      </Stack>
    </Stack>
  );
};

export const formatFormData = (data: CodeData) => {
  return {
    description: data.description,
    isAutomatic: data.executionMethod === 'automatic',
    action: 'code',
    assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
    values: {
      inputs: data.inputs,
      code: data.code,
    },
  };
};

const sidebarLocale = {
  en: {
    infoTitle: 'About Code action',
    infoText:
      'You can write your own python code for various functions or use it to analyse and manipulate field data from the Journey.\n\nThe Output "token" can be used in other actions.',
    createCustomPrompt: 'Create a custom prompt',
    selectField: 'Select field/input you want to analyse',
    infoOutputText: 'Outputs tokens based on your code',
    actionDetails: 'Action details',
    triggerMethod: 'Trigger method',
    input: 'Input',
    inputs: 'Inputs',
    code: 'Code',
    openCodeEditor: 'Open code editor',
    output: 'Output',
    noOutputConfigured: 'No output configured - Open the code editor to configure',
    useOutput: 'You can now use the output in the following actions',
  },
};
